import React from "react";
import { Helmet } from "react-helmet";
import { Controller, useFieldArray } from "react-hook-form";
import {
  businessMatchingTermsOfServicePath,
  privacyPolicyPath,
} from "../../../../../routes";
import Button from "../../../shared/components/ui/Button";
import FormLabel from "../../../shared/components/ui/FormLabel";
import InputError from "../../../shared/components/ui/InputError";
import Checkbox from "../../../shared/components/ui/Checkbox";
import { Box } from "../../../shared/components/ui/chakra";
import SectionTitle from "../../../shared/components/app/SectionTitle";
import SiteSeal from "../../../shared/components/app/SiteSeal";
import AddIcon from "../../../shared/components/icons/AddIcon";
import { Options } from "../../../shared/lib/types";
import { Step2FormData, useStep2Form } from "../lib/useStep2Form";
import DesigendArchitecturalFormForNew from "./DesignedArchitecturalFormForNew";
import BuildingTypeStructureTypeFieldForNew from "./BuildingTypeStructureTypeFieldForNew";
import {
  ClientInfoDesiredBusinessServiceNoteField,
  ContractorInfoSupportedBusinessServiceNoteField,
  IntroductionField,
  MainClientField,
  UseCaseField,
} from "./FormFields";
import { onCheckAllPrefecture } from "../lib/onCheckAllPrefecture";
import {
  createUnifiedFile,
  UnifiedFile,
} from "../../../../shared/lib/UnifiedFile";
import { GroupedPrefectures, SharedArchitecturalSoftwares } from "../../../../shared/lib/types";

import { CheckboxGroup, Divider, Flex, Heading, HStack, Link, Stack, Text, VStack } from "@chakra-ui/react";

const Step2 = ({
  methods,
  architecturalSoftwares,
  industries,
  businessFields,
  groupedPrefectures,
  prefectures,
  buildingTypes,
  structureTypes,
  designedArchitecturalWorksImages,
  setDesigendArchitecturalWorkImages,
  onSubmit,
  onClickBack,
}: {
  methods: ReturnType<typeof useStep2Form>;
  architecturalSoftwares: SharedArchitecturalSoftwares;
  industries: Options;
  businessFields: Options;
  groupedPrefectures: GroupedPrefectures;
  prefectures: Options;
  buildingTypes: Options;
  structureTypes: Options;
  designedArchitecturalWorksImages: UnifiedFile[][];
  setDesigendArchitecturalWorkImages: React.Dispatch<
    React.SetStateAction<UnifiedFile[][]>
  >;
  onSubmit: (data: Step2FormData) => void;
  onClickBack: () => void;
}) => {
  const {
    control,
    formState: { errors, isSubmitting },
    watch,
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "contractor_info_attributes.architectural_design_works_attributes",
  });
  const useCase = watch("use_case");

  return (
    <Box>
      <Helmet>
        <title>
          事業者情報詳細 登録｜建築士のためのコミュニティA-Loop【エーループ】
        </title>
      </Helmet>
      <Heading as="h1" size="lg" textAlign="center">
        <Box>事業者情報 詳細登録</Box>
        <Box>（業務内容、設計実績など）</Box>
      </Heading>
      <Box as="form" onSubmit={methods.handleSubmit(onSubmit)}>
        <Box as="section" mt={14}>
          <SectionTitle>受発注の利用用途</SectionTitle>
          <Box as="fieldset" mt={8}>
            <Box as="legend">
              <FormLabel required>利用用途</FormLabel>
            </Box>
            <Box mt={2}>
              <Controller
                name="use_case"
                control={control}
                render={({ field, fieldState }) => (
                  <UseCaseField field={field} fieldState={fieldState} />
                )}
              />
            </Box>
          </Box>
        </Box>
        <Box as="section" mt={14}>
          <SectionTitle>事業者プロフィール</SectionTitle>
          <Stack mt={8} gap={7}>
            <Controller
              control={control}
              name="introduction"
              render={({ field, fieldState }) => (
                <IntroductionField field={field} fieldState={fieldState} />
              )}
            />
            <Box as="fieldset">
              <Box as="legend">
                <FormLabel>利用している建築系ソフトウェア</FormLabel>
              </Box>
              <Stack mt={2} gap={4}>
                {architecturalSoftwares.map(({ category, items }) => (
                  <Box as="fieldset" key={category}>
                    <Box
                      as="legend"
                      fontSize="sm"
                      pb={1}
                      borderBottom="1px #C4CCCA solid"
                      w="full"
                    >
                      {category}
                    </Box>
                    <Controller
                      control={control}
                      name="used_architectural_software_ids"
                      render={({
                        field: { ref, ...field },
                        fieldState: { error },
                      }) => (
                        <Box mt={2}>
                          <CheckboxGroup {...field}>
                            <Stack>
                              {items.map((item) => (
                                <Checkbox
                                  key={item.id}
                                  value={item.id.toString()}
                                  ref={ref}
                                  isInvalid={!!error}
                                  w="fit-content"
                                >
                                  {item.name}
                                </Checkbox>
                              ))}
                            </Stack>
                          </CheckboxGroup>
                        </Box>
                      )}
                    />
                  </Box>
                ))}
                {errors.used_architectural_software_ids && (
                  <InputError>
                    {errors.used_architectural_software_ids.message}
                  </InputError>
                )}
              </Stack>
            </Box>
            <Controller
              control={control}
              name="main_client"
              render={({ field, fieldState }) => (
                <MainClientField field={field} fieldState={fieldState} />
              )}
            />
          </Stack>
        </Box>
        <Box
          mt={14}
          display={
            useCase === "contractor" || useCase === "both" ? "block" : "none"
          }
        >
          <SectionTitle>受注したい業務</SectionTitle>
          <Stack mt={8} gap={7}>
            <Box as="fieldset">
              <Box as="legend">
                <FormLabel required>受注したい業種</FormLabel>
              </Box>
              <Box mt={2}>
                <Controller
                  control={control}
                  name="contractor_info_attributes.desired_industry_ids"
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <Box>
                      <CheckboxGroup {...field}>
                        <Stack>
                          {industries.map((industry) => (
                            <Checkbox
                              key={industry.id}
                              value={industry.id.toString()}
                              ref={ref}
                              isInvalid={!!error}
                              w="fit-content"
                            >
                              {industry.name}
                            </Checkbox>
                          ))}
                        </Stack>
                      </CheckboxGroup>
                      {error && <InputError>{error?.message}</InputError>}
                    </Box>
                  )}
                />
              </Box>
            </Box>
            <Box as="fieldset">
              <Box as="legend">
                <FormLabel required>対応できる分野</FormLabel>
              </Box>
              <Box mt={2}>
                <Controller
                  control={control}
                  name="contractor_info_attributes.supported_business_field_ids"
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <Box>
                      <CheckboxGroup {...field}>
                        <Stack>
                          {businessFields.map((businessField) => (
                            <Checkbox
                              value={businessField.id.toString()}
                              ref={ref}
                              key={businessField.id}
                              isInvalid={!!error}
                            >
                              {businessField.name}
                            </Checkbox>
                          ))}
                        </Stack>
                      </CheckboxGroup>
                      {error && <InputError>{error?.message}</InputError>}
                    </Box>
                  )}
                />
              </Box>
            </Box>
            <Controller
              control={control}
              name="contractor_info_attributes.supported_business_service_note"
              render={({ field, fieldState }) => (
                <ContractorInfoSupportedBusinessServiceNoteField
                  field={field}
                  fieldState={fieldState}
                />
              )}
            />
            <BuildingTypeStructureTypeFieldForNew
              methods={methods}
              buildingTypes={buildingTypes}
              structureTypes={structureTypes}
            />
            <Box as="fieldset">
              <Box as="legend">
                <FormLabel required>対応可能エリア</FormLabel>
              </Box>
              <Box mt={2}>
                <Controller
                  control={control}
                  name="contractor_info_attributes.supported_prefecture_ids"
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <Box>
                      <CheckboxGroup {...field}>
                        <Stack gap={4}>
                          <Checkbox
                            onChange={(e) => {
                              onCheckAllPrefecture({
                                checked: e.target.checked,
                                onChange: field.onChange,
                                value: field.value,
                                groupedPrefectures,
                              });
                            }}
                          >
                            全国
                          </Checkbox>
                          {groupedPrefectures.map(({ region, prefectures }) => (
                            <Box as="fieldset" key={region}>
                              <Box
                                as="legend"
                                w="full"
                                pb={1}
                                borderBottom="1px #C4CCCA solid"
                                fontSize="xs"
                              >
                                {region}
                              </Box>
                              <HStack mt={3} wrap="wrap" gap={3}>
                                {prefectures.map((prefecture) => (
                                  <Checkbox
                                    isInvalid={!!error}
                                    key={prefecture.id}
                                    value={prefecture.id.toString()}
                                    ref={ref}
                                    disabled={field.value.length === 47}
                                  >
                                    {prefecture.name}
                                  </Checkbox>
                                ))}
                              </HStack>
                            </Box>
                          ))}
                        </Stack>
                      </CheckboxGroup>
                      {error && <InputError>{error?.message}</InputError>}
                    </Box>
                  )}
                />
              </Box>
            </Box>
            <Box as="fieldset">
              <Box as="legend">
                <FormLabel required>設計実績</FormLabel>
              </Box>
              <Stack mt={3} gap={4}>
                {fields.map((field, index) => (
                  <Box key={field.id} py={4} px={5} bgColor="brand.50">
                    <Box as="fieldset">
                      <Box as="legend">
                        <FormLabel>実績 {index + 1}</FormLabel>
                      </Box>
                      <Box mt={4}>
                        <DesigendArchitecturalFormForNew
                          methods={methods}
                          namePrefix={`contractor_info_attributes.architectural_design_works_attributes.${index}`}
                          buildingTypes={buildingTypes}
                          structureTypes={structureTypes}
                          prefectures={prefectures}
                          defaultImages={designedArchitecturalWorksImages[
                            index
                          ].map((file) => ({
                            url: file.url,
                            signed_id: file.signedId,
                          }))}
                          onChange={(files) => {
                            setDesigendArchitecturalWorkImages((prev) => {
                              const result = [...prev];
                              result[index] = files.map((file) =>
                                createUnifiedFile(file),
                              );
                              return result;
                            });
                          }}
                        />
                      </Box>
                      {index > 0 && (
                        <Button
                          color="red"
                          onClick={() => {
                            remove(index);
                            setDesigendArchitecturalWorkImages((prev) => {
                              const result = [...prev];
                              result.splice(index, 1);
                              return result;
                            });
                          }}
                        >
                          実績を削除
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
              {fields.length < 3 && (
                <Button
                  mt={3}
                  variant="outline"
                  leftIcon={<AddIcon />}
                  onClick={() => {
                    append({
                      project_name: "",
                      building_type_id: "",
                      structure_type_ids: [],
                      project_detail: "",
                      prefecture_id: "",
                      above_ground_floors: "",
                      underground_floors: "",
                      total_floor_area: "",
                      completion_year: "",
                      published: true,
                    });
                    setDesigendArchitecturalWorkImages((prev) => [...prev, []]);
                  }}
                >
                  実績を追加
                </Button>
              )}
            </Box>
          </Stack>
        </Box>
        <Box
          as="section"
          mt={14}
          display={
            useCase === "client" || useCase === "both" ? "block" : "none"
          }
        >
          <SectionTitle>発注したい業務</SectionTitle>
          <Stack mt={8} gap={7}>
            <Box as="fieldset">
              <Box as="legend">
                <FormLabel required>発注したい業種</FormLabel>
              </Box>
              <Box mt={2}>
                <Controller
                  control={control}
                  name="client_info_attributes.desired_industry_ids"
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <Box>
                      <CheckboxGroup {...field}>
                        <Stack>
                          {industries.map((industry) => (
                            <Checkbox
                              key={industry.id}
                              value={industry.id.toString()}
                              ref={ref}
                              isInvalid={!!error}
                            >
                              {industry.name}
                            </Checkbox>
                          ))}
                        </Stack>
                      </CheckboxGroup>
                      {error && <InputError>{error?.message}</InputError>}
                    </Box>
                  )}
                />
              </Box>
            </Box>

            <Box as="fieldset">
              <Box as="legend">
                <FormLabel required>発注したい分野</FormLabel>
              </Box>
              <Box mt={2}>
                <Controller
                  control={control}
                  name="client_info_attributes.desired_business_field_ids"
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <Box>
                      <CheckboxGroup {...field}>
                        <Stack>
                          {businessFields.map((businessField) => (
                            <Checkbox
                              key={businessField.id}
                              value={businessField.id.toString()}
                              ref={ref}
                              isInvalid={!!error}
                            >
                              {businessField.name}
                            </Checkbox>
                          ))}
                        </Stack>
                      </CheckboxGroup>
                      {error && <InputError>{error?.message}</InputError>}
                    </Box>
                  )}
                />
              </Box>
            </Box>

            <Controller
              control={control}
              name="client_info_attributes.desired_business_service_note"
              render={({ field, fieldState }) => (
                <ClientInfoDesiredBusinessServiceNoteField
                  field={field}
                  fieldState={fieldState}
                />
              )}
            />
          </Stack>
        </Box>
        <Divider
          border="1px var(--chakra-colors-brand-500) solid"
          opacity={1}
          mt={12}
        />
        <VStack mt={{ base: 8, md: 12 }} textAlign="center">
          <VStack gap={2}>
            <Controller
              name="agreement"
              control={control}
              render={({ field: { value, ...field }, fieldState }) => (
                <>
                  <Checkbox {...field} isChecked={value} w="fit-content">
                    <Text>
                      「利用規約」および「個人情報保護方針」に同意する
                    </Text>
                  </Checkbox>
                  {fieldState.error !== undefined && (
                    <InputError pl={6}>{fieldState.error.message}</InputError>
                  )}
                </>
              )}
            />
            <Text fontSize="xs" p={2} bgColor="#F2F2F2">
              「
              <Link
                color="textLink"
                textDecoration="underline"
                _hover={{ textDecoration: "none" }}
                href={businessMatchingTermsOfServicePath()}
                target="_blank"
                rel="noreferrer"
              >
                利用規約
              </Link>
              」「
              <Link
                color="textLink"
                textDecoration="underline"
                _hover={{ textDecoration: "none" }}
                href={privacyPolicyPath()}
                target="_blank"
                rel="noreferrer"
              >
                個人情報保護方針
              </Link>
              」をご確認いただき、チェックをお願いします。
            </Text>
          </VStack>
          <Divider
            border="1px var(--chakra-colors-brand-500) dashed"
            opacity={1}
            mt={8}
          />
          <Box>
            <Flex mt={4} gap={3}>
              <Button
                type="button"
                isDisabled={isSubmitting}
                onClick={onClickBack}
                variant="outline"
              >
                <Box wordBreak="keep-all" overflowWrap="anywhere">
                  事業者情報の
                  <wbr />
                  入力に戻る
                </Box>
              </Button>
              <Button type="submit" isLoading={isSubmitting}>
                <Box wordBreak="keep-all" overflowWrap="anywhere">
                  入力内容の
                  <wbr />
                  確認へ進む
                </Box>
              </Button>
            </Flex>
          </Box>
          <Box mt={4}>
            <SiteSeal />
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default Step2;
